import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { AsyncButton } from "./ui/async-button.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card.js";
import { Label } from "./ui/label.js";
import { SelectField } from "./ui/select-field.js";
import { Textarea } from "./ui/textarea.js";
import { CompCompareTable } from "./CompCompareTable.js";
import {
  COMP_STATUS_BADGE, REVIEW_ACTION_LABELS, asNum, fmtAmt, fmtDate, humanize,
  type BreakdownAmount, type CompStatus,
} from "../lib/compensation.js";

export interface CompReview {
  id: string; action: string; comment: string | null; performedBy: string; createdAt: string;
}

/** GET /employees/:id/compensation/snapshots/:snapshotId */
export interface CompSnapshotDetail {
  id:                     string;
  ctcAnnual:              number | string;
  bonusAnnual:            number | string;
  taxRegime:              "NEW_REGIME" | "OLD_REGIME";
  professionalTaxMonthly: number | string;
  includeMedicalInsurance?: boolean;
  effectiveFrom:          string;
  effectiveTo:            string | null;
  reason:                 string;
  status:                 CompStatus;
  createdBy:              string;
  submittedBy:            string | null;
  submittedAt:            string | null;
  verifiedBy:             string | null;
  verifiedAt:             string | null;
  breakdown:              BreakdownAmount[];
  reviews:                CompReview[];
  userNames:              Record<string, string>;
  canVerify:              boolean;
}

/** A row of GET /employees/:id/compensation/snapshots, used for the "Compare with" picker. */
interface CompareOption { id: string; ctcAnnual: number | string; effectiveFrom: string; reason: string; status: CompStatus }

interface Props {
  employeeId: string;
  /** The pending or rejected revision. */
  snapshotId: string;
  /** The latest approved revision it would replace, if any. */
  baselineId: string | null;
  /** Viewer is in COMP_VERIFY_ROLES (Finance). */
  isVerifier: boolean;
  /** Viewer may edit / withdraw (SALARY_ROLES, not read-only). */
  canEditComp: boolean;
  onChanged: () => void;
  onEdit?: (detail: CompSnapshotDetail) => void;
}

export function CompVerificationPanel({ employeeId, snapshotId, baselineId, isVerifier, canEditComp, onChanged, onEdit }: Props) {
  const [detail, setDetail]     = useState<CompSnapshotDetail | null>(null);
  const [baseline, setBaseline] = useState<CompSnapshotDetail | null>(null);
  const [loadErr, setLoadErr]   = useState<string | null>(null);
  const [comment, setComment]   = useState("");
  const [err, setErr]           = useState<string | null>(null);
  // Which verified revision the proposal is compared with. Defaults to the latest one
  // (the revision it replaces); Finance can switch to any earlier verified revision.
  const [compareId, setCompareId] = useState<string | null>(baselineId);
  const [approved, setApproved]   = useState<CompareOption[]>([]);
  useEffect(() => { setCompareId(baselineId); }, [baselineId]);

  const base = `/employees/${employeeId}/compensation/snapshots`;
  useEffect(() => {
    let cancelled = false;
    setDetail(null); setLoadErr(null); setComment(""); setErr(null);
    Promise.all([
      api.get<CompSnapshotDetail>(`${base}/${snapshotId}`),
      api.get<CompareOption[]>(base).catch(() => ({ data: [] as CompareOption[] })), // options only; the compare still works without it
    ])
      .then(([d, list]) => {
        if (cancelled) return;
        setDetail(d.data);
        setApproved(list.data.filter(h => h.status === "APPROVED" && h.id !== snapshotId));
      })
      .catch(e => { logError("CompVerificationPanel.load", e); if (!cancelled) setLoadErr(errMsg(e) ?? "Couldn't load this revision."); });
    return () => { cancelled = true; };
  }, [base, snapshotId]);

  // The comparison side loads on its own, so switching it never clears the Finance comment.
  useEffect(() => {
    let cancelled = false;
    setBaseline(null);
    if (!compareId) return;
    api.get<CompSnapshotDetail>(`${base}/${compareId}`)
      .then(r => { if (!cancelled) setBaseline(r.data); })
      .catch(e => { logError("CompVerificationPanel.loadBaseline", e); if (!cancelled) setLoadErr(errMsg(e) ?? "Couldn't load the revision to compare with."); });
    return () => { cancelled = true; };
  }, [base, compareId]);

  async function decide(action: "approve" | "reject") {
    if (action === "reject" && !comment.trim()) {
      setErr("Add a comment saying what HR needs to change.");
      throw new Error("validation");
    }
    setErr(null);
    try {
      await api.post(`/employees/${employeeId}/compensation/snapshots/${snapshotId}/${action}`,
        { comment: comment.trim() || undefined });
      onChanged();
    } catch (e) {
      logError(`CompVerificationPanel.${action}`, e);
      setErr(errMsg(e) ?? "Something went wrong. Please try again.");
      throw e;
    }
  }

  async function withdraw() {
    setErr(null);
    try {
      await api.post(`/employees/${employeeId}/compensation/snapshots/${snapshotId}/withdraw`);
      onChanged();
    } catch (e) {
      logError("CompVerificationPanel.withdraw", e);
      setErr(errMsg(e) ?? "Couldn't withdraw this revision.");
      throw e;
    }
  }

  if (loadErr) return <p className="m-0 py-3 text-[13px] text-[var(--color-error-fg)]">{loadErr}</p>;
  if (!detail) return <p className="m-0 py-3 text-[13px] text-surface-400">Loading revision…</p>;

  const badge = COMP_STATUS_BADGE[detail.status];
  const name = (id: string | null) => (id ? detail.userNames[id] ?? "Unknown user" : "—");
  const lastRejection = [...detail.reviews].reverse().find(r => r.action === "REJECTED");
  const pending = detail.status === "PENDING_VERIFICATION";
  const ownSubmission = isVerifier && pending && !detail.canVerify;

  return (
    <Card className="mb-3 border-[var(--color-warning-border)]">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>₹{fmtAmt(asNum(detail.ctcAnnual))} CTC</CardTitle>
          <Badge tone={badge.tone}>{badge.label}</Badge>
        </div>
        <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-x-3 gap-y-1">
          {[
            { label: "Effective from", value: fmtDate(detail.effectiveFrom) },
            { label: "Reason", value: humanize(detail.reason) },
            { label: "Tax regime", value: humanize(detail.taxRegime) },
            ...(detail.submittedAt ? [{ label: "Submitted by", value: `${name(detail.submittedBy)} · ${fmtDate(detail.submittedAt)}` }] : []),
          ].map(f => (
            <div key={f.label}>
              <dt className="text-[10px] font-bold uppercase tracking-[0.05em] text-surface-400">{f.label}</dt>
              <dd className="m-0 text-[12px] font-medium text-surface-800">{f.value}</dd>
            </div>
          ))}
        </dl>
        <CardDescription className="text-[12px]">
          {pending
            ? "These figures are unconfirmed. The current verified compensation stays in effect until Finance approves them."
            : detail.status === "APPROVED"
              ? "Finance approved this revision; it is locked and takes effect from its effective date."
              : detail.status === "WITHDRAWN"
                ? "HR withdrew this revision before it was decided. It never took effect."
                : "Finance sent this revision back. The current verified compensation is still in effect."}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {lastRejection?.comment && detail.status === "REJECTED" && (
          <p className="m-0 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-3 py-2 text-[12px] text-[var(--color-error-fg)]">
            <span className="font-semibold">{name(lastRejection.performedBy)}:</span> {lastRejection.comment}
          </p>
        )}

        {approved.length > 1 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12px] text-surface-500">Compare with</span>
            <SelectField size="sm" className="w-auto" value={compareId ?? ""} onChange={e => setCompareId(e.target.value || null)}>
              {approved.map(a => (
                <option key={a.id} value={a.id}>
                  {fmtDate(a.effectiveFrom)} · ₹{fmtAmt(asNum(a.ctcAnnual))} · {humanize(a.reason)}{a.id === baselineId ? " (current)" : ""}
                </option>
              ))}
            </SelectField>
          </div>
        )}

        {compareId && !baseline ? (
          <p className="m-0 py-2 text-[12px] text-surface-400">Loading comparison…</p>
        ) : (
          <CompCompareTable
            before={compareId ? baseline : null}
            after={detail}
            beforeLabel={compareId === baselineId ? "Current (verified)" : "Earlier (verified)"}
            afterLabel={pending ? "Proposed" : "Rejected"}
          />
        )}

        {/* Activity is always shown — no expand/collapse. */}
        {detail.reviews.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.05em] text-surface-400">Activity</div>
            <ol className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0 text-[12px] text-surface-600">
              {detail.reviews.map(r => (
                <li key={r.id}>
                  <span className="font-semibold text-surface-800">{REVIEW_ACTION_LABELS[r.action] ?? humanize(r.action)}</span>
                  {" · "}{name(r.performedBy)} · {fmtDate(r.createdAt)}
                  {r.comment && <span className="text-surface-500"> — “{r.comment}”</span>}
                </li>
              ))}
            </ol>
          </div>
        )}

        {ownSubmission && (
          <p className="m-0 text-[12px] text-surface-500">You prepared this revision, so someone else in Finance has to verify it.</p>
        )}

        {detail.canVerify && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`comp-verify-comment-${detail.id}`}>Finance comment</Label>
            <Textarea
              id={`comp-verify-comment-${detail.id}`}
              rows={2}
              placeholder="Required when rejecting; optional when approving"
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </div>
        )}

        {err && <p className="m-0 text-[12px] text-[var(--color-error-fg)]">{err}</p>}

        <div className="flex flex-wrap gap-2">
          {detail.canVerify && (
            <>
              <AsyncButton
                variant="success"
                onClick={() => decide("approve")}
                confirm={{
                  title: "Approve this compensation?",
                  description: "It takes effect from its effective date and is locked. Any later change needs a new revision.",
                  actionText: "Approve & Lock",
                }}
              >
                Approve & Lock
              </AsyncButton>
              <AsyncButton
                variant="destructive"
                onClick={() => decide("reject")}
                confirm={{
                  title: "Reject this compensation?",
                  description: "It goes back to HR with your comment. The current verified figures stay in effect.",
                  actionText: "Reject",
                }}
              >
                Reject
              </AsyncButton>
            </>
          )}
          {canEditComp && detail.status === "REJECTED" && onEdit && (
            <Button type="button" onClick={() => onEdit(detail)}>Edit & Resubmit</Button>
          )}
          {canEditComp && (
            <AsyncButton
              variant="outline"
              animated={false}
              onClick={() => withdraw()}
              confirm={{
                title: "Withdraw this revision?",
                description: "It stays in the history as withdrawn and is no longer sent to Finance.",
                actionText: "Withdraw",
              }}
            >
              Withdraw
            </AsyncButton>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
